import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Body2,
  Spinner,
} from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";
import { getYouTrackBreakdown } from "../../services/expectedHoursService";
import type { IYouTrackProjectHoursLine } from "../../services/types/expectedHours";
import appErrorToast from "../common/AppErrorToast";
import { payoutBreakdownStyles } from "./PayoutBreakdownInfoButtonStyles";

interface YouTrackBreakdownInfoButtonProps {
  userId: string;
  year: number;
  month: number;
  ariaLabel: string;
}

const cache = new Map<string, IYouTrackProjectHoursLine[]>();

function cacheKey(userId: string, year: number, month: number): string {
  return `${userId}|${year}|${month}`;
}

const YouTrackBreakdownInfoButton: React.FC<YouTrackBreakdownInfoButtonProps> = ({
  userId,
  year,
  month,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<IYouTrackProjectHoursLine[] | null>(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const key = cacheKey(userId, year, month);
    const cached = cache.get(key);
    if (cached) {
      if (mounted.current) setLines(cached);
      if (mounted.current) setLoading(false);
      return;
    }

    try {
      const data = await getYouTrackBreakdown(userId, year, month);
      cache.set(key, data);
      if (mounted.current) setLines(data);
    } catch {
      appErrorToast("Nie udało się pobrać szczegółów YouTrack.");
      if (mounted.current) setLines([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [userId, year, month]);

  useEffect(() => {
    if (!open) return;
    setLines(null);
    setLoading(true);
    void load();
  }, [open, load]);

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open || false)}>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<InfoRegular />}
          aria-label={ariaLabel}
        />
      </PopoverTrigger>
      <PopoverSurface tabIndex={-1} style={payoutBreakdownStyles.popoverSurface}>
        <Body2 style={payoutBreakdownStyles.title}>YouTrack</Body2>
        {loading && (
          <div style={payoutBreakdownStyles.spinnerRow}>
            <Spinner size="small" />
          </div>
        )}
        {!loading && lines && lines.length === 0 && (
          <Body2>Brak pozycji do wyświetlenia.</Body2>
        )}
        {!loading && lines && lines.length > 0 && (
          <table style={payoutBreakdownStyles.table}>
            <thead>
              <tr>
                <th style={payoutBreakdownStyles.th}>Projekt</th>
                <th style={payoutBreakdownStyles.th}>Liczba godzin</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.projectName}>
                  <td style={payoutBreakdownStyles.td}>{line.projectName}</td>
                  <td style={payoutBreakdownStyles.td}>{line.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PopoverSurface>
    </Popover>
  );
};

export default YouTrackBreakdownInfoButton;
